import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Power, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_OPTIONS = [
  { value: "admin", label: "📋 Info. Administrativas" },
  { value: "morning", label: "🌅 Atividades Manhã" },
  { value: "lunch", label: "🍽️ Parada Almoço" },
  { value: "afternoon", label: "🌇 Atividades Tarde" },
  { value: "photos", label: "📸 Fotos & Comentários" },
  { value: "activities", label: "📊 Dados Específicos" },
  { value: "impediments", label: "⚠️ Impedimentos" },
  { value: "warehouse", label: "📦 Almoxarifado" },
  { value: "closing", label: "✅ Encerramento" },
  { value: "demands", label: "📝 Demandas Administrativas" },
];

const TYPE_OPTIONS = [
  { value: "text",   label: "Texto livre" },
  { value: "number", label: "Número" },
  { value: "time",   label: "Tempo (HH:MM)" },
  { value: "yes_no", label: "Sim / Não" },
  { value: "select", label: "Lista de opções" },
];

const sectionLabel = (v) => SECTION_OPTIONS.find(o => o.value === v)?.label || v;
const typeLabel = (v) => TYPE_OPTIONS.find(o => o.value === v)?.label || v;

const emptyForm = {
  label: "",
  type: "text",
  target_section: "admin",
  description: "",
  unit: "",
  select_options_text: "",
  standard_value: "",
  tolerance_percentage: "",
  tolerance_minutes: "",
  expected_yes_no: "true",
  is_required: false,
  is_active: true,
};

const CustomQuestionsManager = () => {
  const [questions, setQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/custom-questions`);
      setQuestions(res.data || []);
    } catch (e) {
      toast.error("Erro ao carregar perguntas");
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      label: q.label || "",
      type: q.type || "text",
      target_section: q.target_section || "admin",
      description: q.description || "",
      unit: q.unit || "",
      select_options_text: (q.select_options || []).join("\n"),
      standard_value: q.standard_value || "",
      tolerance_percentage: q.tolerance_percentage ?? "",
      tolerance_minutes: q.tolerance_minutes ?? "",
      expected_yes_no: q.expected_yes_no === false ? "false" : "true",
      is_required: !!q.is_required,
      is_active: q.is_active !== false,
    });
    setShowModal(true);
  };

  const buildPayload = () => {
    const payload = {
      label: form.label.trim(),
      type: form.type,
      target_section: form.target_section,
      description: form.description?.trim() || null,
      unit: form.unit?.trim() || null,
      select_options: form.type === "select"
        ? form.select_options_text.split("\n").map(s => s.trim()).filter(Boolean)
        : [],
      standard_value: form.standard_value?.toString().trim() || null,
      tolerance_percentage: form.type === "number" && form.tolerance_percentage !== ""
        ? parseFloat(form.tolerance_percentage)
        : null,
      tolerance_minutes: form.type === "time" && form.tolerance_minutes !== ""
        ? parseInt(form.tolerance_minutes, 10)
        : null,
      expected_yes_no: form.type === "yes_no"
        ? form.expected_yes_no === "true"
        : null,
      is_required: !!form.is_required,
      is_active: !!form.is_active,
    };
    return payload;
  };

  const save = async () => {
    if (!form.label.trim()) {
      toast.error("Informe o texto da pergunta");
      return;
    }
    if (form.type === "select" && !form.select_options_text.trim()) {
      toast.error("Informe ao menos uma opção (uma por linha)");
      return;
    }
    try {
      const payload = buildPayload();
      if (editing) {
        await axios.put(`${API}/custom-questions/${editing.id}`, payload);
        toast.success("Pergunta atualizada");
      } else {
        await axios.post(`${API}/custom-questions`, payload);
        toast.success("Pergunta criada");
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar pergunta");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Excluir pergunta "${q.label}"?\nRespostas em RDOs anteriores serão preservadas, mas a pergunta deixa de aparecer.`)) return;
    try {
      await axios.delete(`${API}/custom-questions/${q.id}`);
      toast.success("Pergunta excluída");
      load();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const toggleActive = async (q) => {
    try {
      await axios.patch(`${API}/custom-questions/${q.id}/toggle-active`);
      load();
    } catch (e) {
      toast.error("Erro ao alterar status");
    }
  };

  const move = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[idx], next[target]] = [next[target], next[idx]];
    setQuestions(next);
    try {
      await axios.post(`${API}/custom-questions/reorder`, { order: next.map(q => q.id) });
    } catch (e) {
      toast.error("Erro ao reordenar — recarregando");
      load();
    }
  };

  return (
    <Card data-testid="custom-questions-manager">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600" /> Perguntas Customizadas
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Perguntas globais que aparecem nas abas-alvo de todos os RDOs (admin define).
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-custom-question-btn" className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1" /> Nova Pergunta
        </Button>
      </CardHeader>
      <CardContent>
        {questions.length === 0 ? (
          <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg" data-testid="custom-questions-empty">
            Nenhuma pergunta cadastrada. Clique em "Nova Pergunta" para criar a primeira.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-slate-600">
                  <th className="py-2 w-10">#</th>
                  <th className="py-2">Pergunta</th>
                  <th className="py-2">Aba alvo</th>
                  <th className="py-2">Tipo</th>
                  <th className="py-2">Padrão</th>
                  <th className="py-2 text-center">Status</th>
                  <th className="py-2 text-center w-[180px]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q, idx) => (
                  <tr key={q.id} className={`border-b hover:bg-slate-50 ${!q.is_active ? "opacity-60" : ""}`} data-testid={`custom-question-row-${q.id}`}>
                    <td className="py-2">{idx + 1}</td>
                    <td className="py-2">
                      <div className="font-medium">{q.label}</div>
                      {q.is_required && <Badge variant="outline" className="text-[10px] mt-1">obrigatória</Badge>}
                    </td>
                    <td className="py-2 text-xs">{sectionLabel(q.target_section)}</td>
                    <td className="py-2 text-xs">
                      {typeLabel(q.type)}
                      {q.unit ? <span className="text-slate-400"> ({q.unit})</span> : null}
                    </td>
                    <td className="py-2 text-xs font-mono">
                      {q.standard_value ?? "—"}
                      {q.type === "number" && q.tolerance_percentage != null && <span className="text-slate-400"> ±{q.tolerance_percentage}%</span>}
                      {q.type === "time"   && q.tolerance_minutes != null    && <span className="text-slate-400"> ±{q.tolerance_minutes}min</span>}
                      {q.type === "yes_no" && q.expected_yes_no != null      && <span className="text-slate-400"> esperado: {q.expected_yes_no ? "Sim" : "Não"}</span>}
                    </td>
                    <td className="py-2 text-center">
                      <Badge className={q.is_active ? "bg-green-600" : "bg-gray-400"}>
                        {q.is_active ? "Ativa" : "Inativa"}
                      </Badge>
                    </td>
                    <td className="py-2">
                      <div className="flex justify-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`up-btn-${q.id}`} title="Mover para cima">
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => move(idx, +1)} disabled={idx === questions.length - 1} data-testid={`down-btn-${q.id}`} title="Mover para baixo">
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(q)} data-testid={`toggle-btn-${q.id}`} title={q.is_active ? "Inativar" : "Ativar"}>
                          <Power className={`w-4 h-4 ${q.is_active ? "text-green-600" : "text-gray-400"}`} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(q)} data-testid={`edit-btn-${q.id}`} title="Editar">
                          <Edit2 className="w-4 h-4 text-slate-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(q)} data-testid={`remove-btn-${q.id}`} title="Excluir">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Modal de criação/edição */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar pergunta" : "Nova pergunta"}</DialogTitle>
            <DialogDescription>
              Perguntas aparecem na aba escolhida de todos os RDOs.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Texto da pergunta *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder='Ex.: "Quantos colaboradores estiveram em campo?"'
                data-testid="cq-label-input"
              />
            </div>
            <div>
              <Label>Descrição auxiliar (opcional)</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Texto que aparece abaixo do campo, ajudando o usuário a responder."
                data-testid="cq-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Aba alvo *</Label>
                <Select value={form.target_section} onValueChange={(v) => setForm({ ...form, target_section: v })}>
                  <SelectTrigger data-testid="cq-section-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SECTION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de resposta *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="cq-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.type === "number" && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Unidade</Label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="h, m², kg, %…" data-testid="cq-unit-input" />
                </div>
                <div>
                  <Label>Padrão (Dashboard)</Label>
                  <Input type="number" value={form.standard_value} onChange={(e) => setForm({ ...form, standard_value: e.target.value })} placeholder="Ex.: 8.0" data-testid="cq-standard-input" />
                </div>
                <div>
                  <Label>Tolerância ±%</Label>
                  <Input type="number" value={form.tolerance_percentage} onChange={(e) => setForm({ ...form, tolerance_percentage: e.target.value })} placeholder="10" data-testid="cq-tol-pct-input" />
                </div>
              </div>
            )}

            {form.type === "time" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Padrão HH:MM (Dashboard)</Label>
                  <Input type="time" value={form.standard_value} onChange={(e) => setForm({ ...form, standard_value: e.target.value })} data-testid="cq-standard-time-input" />
                </div>
                <div>
                  <Label>Tolerância ± minutos</Label>
                  <Input type="number" value={form.tolerance_minutes} onChange={(e) => setForm({ ...form, tolerance_minutes: e.target.value })} placeholder="15" data-testid="cq-tol-min-input" />
                </div>
              </div>
            )}

            {form.type === "yes_no" && (
              <div>
                <Label>Resposta esperada (Dashboard)</Label>
                <Select value={form.expected_yes_no} onValueChange={(v) => setForm({ ...form, expected_yes_no: v })}>
                  <SelectTrigger data-testid="cq-expected-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Sim</SelectItem>
                    <SelectItem value="false">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.type === "select" && (
              <div>
                <Label>Opções (uma por linha) *</Label>
                <Textarea
                  rows={4}
                  value={form.select_options_text}
                  onChange={(e) => setForm({ ...form, select_options_text: e.target.value })}
                  placeholder={"Opção A\nOpção B\nOpção C"}
                  data-testid="cq-options-input"
                />
              </div>
            )}

            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_required} onCheckedChange={(v) => setForm({ ...form, is_required: v })} data-testid="cq-required-switch" />
                Resposta obrigatória
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="cq-active-switch" />
                Ativa (aparece em RDOs novos)
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} data-testid="cq-cancel-btn">Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="cq-save-btn">{editing ? "Atualizar" : "Criar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CustomQuestionsManager;
